/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useQueueList,
  useStats,
  useTournamentConsecutive,
} from "@/lib/hooks/useApi";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useNextStep } from "nextstepjs";
import { NavigationRush } from "@/components/NavigationRush";
import { Countdown } from "@/components/ui/countdown";
import { QueuePlayerSheet } from "@/components/QueuePlayerSheet";
import {
  Activity,
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePrivy } from "@privy-io/react-auth";
import {
  cn,
  formations,
  generatePrizeData,
  generateTextShadow,
  REWARD_ICONS,
} from "@/lib/utils";
import RollingList from "@/components/RollingList";
import { useSolanaPrice } from "@/lib/hooks/useSolanaPrice";
import { RushLiveControl } from "@/components/game/controls/RushLiveControl";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { NavigationTournamentEnded } from "../tournament/-components/NavigationTournamentEnded";
import { UserRankList } from "@/components/UserRankList";
import AnimatedLoading from "@/components/AnimatedLoading";
import ResourceBar from "@/components/ResourceBar";
import ArenaChartECharts from "@/components/ArenaChartECharts";
import { useMusic } from "@/lib/hooks/useMusic";
import { MATCH_TRACK, MENU_TRACK } from "@/lib/configs/music";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import { ClaimBorderDialog } from "@/components/ClaimBorderDialog";
import { DefaultButton } from "@/components/DefaultButton";
import { AvatarBorder } from "@/components/AvatarBorder";
import { isRushLeaderboardFlipped } from "@/lib/constants";
import { t } from "@/lib/i18n";
import { RevealOverlay } from "@/components/RevealOverlay";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { isGameLiveAtom, rushTourModeAtom } from "@/lib/gameStatusAtom";
import type { TournamentDetailsResponse } from "@/lib/types/api";
import configs from "@/lib/configs";
import dayjs from "dayjs";
import { getSyncedNow, updateClockOffset } from "@/lib/clockSync";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { ItemExtraTrophyDialog } from "@/components/ItemExtraTrophyInfoDialog";
import { ReportPlayerDialog } from "@/components/ReportPlayerDialog";
import { ReportModeToggle } from "@/components/ReportModeToggle";
import { useCanReportBatch } from "@/lib/hooks/useApi";
import type { Player } from "@/lib/types/api";

export const Route = createFileRoute("/(game)/rush/")({
  component: Rush,
});

const queueData = {
  hasActiveQueue: true,
  roundCount: 2,
  slotsPerRound: 1,
  currentRound: 1,
  squadMatchType: "long",
  players: {
    squadAddress: "c45ef0bc-45c5-42c6-943a-092f7eef1754",
    squadDetails: {
      name: "Fufu",
      owner: "BxftW8VqSqJG8jJikXAW7itGJD158WyZ4vtw7Mx5cqN5",
      formation: "2-2-1",
      playerDetails: [
        {
          exponent: -8,
          playerName: "Toncoin",
          pythLazerId: 12,
          playerSymbol: "ton",
          positionIndex: 0,
          playerImageUrl:
            "https://coin-images.coingecko.com/coins/images/17980/large/photo_2024-09-10_17.09.00.jpeg?1725963446",
        },
        {
          exponent: -8,
          playerName: "Toncoin",
          pythLazerId: 12,
          playerSymbol: "ton",
          positionIndex: 1,
          playerImageUrl:
            "https://coin-images.coingecko.com/coins/images/17980/large/photo_2024-09-10_17.09.00.jpeg?1725963446",
        },
        {
          exponent: -8,
          playerName: "Story",
          pythLazerId: 309,
          playerSymbol: "ip",
          positionIndex: 2,
          playerImageUrl:
            "https://coin-images.coingecko.com/coins/images/54035/large/Transparent_bg.png?1738075331",
        },
        {
          exponent: -8,
          playerName: "Virtuals Protocol",
          pythLazerId: 107,
          playerSymbol: "virtual",
          positionIndex: 3,
          playerImageUrl:
            "https://coin-images.coingecko.com/coins/images/34057/large/LOGOMARK.png?1708356054",
        },
        {
          exponent: -10,
          playerName: "Bonk",
          pythLazerId: 9,
          playerSymbol: "bonk",
          positionIndex: 4,
          playerImageUrl:
            "https://coin-images.coingecko.com/coins/images/28600/large/bonk.jpg?1696527587",
        },
      ],
      squadMatchType: "long",
    },
  },
};

const data = {
  tournamentId: "dc6dd7bd-08b2-4815-b8b2-0d388ab9af5e",
  tournamentName: "Rush",
  tournamentLogo:
    "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/cfl-rush-mode-logo.svg",
  tournamentPoster:
    "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/rush-trenches-poster.png",
  tournamentBanner:
    "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/rush_banner.png",
  tournamentDescription: "DRAFT. DOMINATE. WIN",
  tournamentAccount: "Eq6uEwk9iJjXLXRtmUjtuXdufn214De5JUJnQzZpDzTT",
  tournamentSeed: "1761659630",
  tournamentType: "rush",
  trophies: 20000,
  maximumPlayer: 1000,
  totalUsdcReward: 0,
  entryUsdc: 0.03,
  entryFreePass: 3,
  startDate: 1761659690,
  endDate: 1761659990,
  duration: 300,
  joinedPlayers: 3,
  status: "live",
  maxMultiEntryCount: 3,
  referralAmountInPercent: 3.6,
  cashbackAmountInPercent: 0.9,
  payoutList: {
    payout: [
      {
        rank: "1st",
        range: {
          min: 1,
          max: 1,
        },
        rewards: [
          {
            amount: "0.076",
            itemId: 0,
          },
          {
            amount: 1500,
            itemId: 1,
          },
        ],
      },
    ],
    totalPrize: "$18.24",
  },
  players: [
    {
      squadAddress: "20e0490b-cd03-4be1-9527-f48c9768a247",
      squadDetails: {
        name: "Squad 1",
        index: 3,
        formation: "0-0-5",
        squadValue: 1.24012572,
        squadMatchType: "long",
        owner: "25AHqZAtU2iZo8RXG3KAzrWTPpK319vcpxBJEuEwpdQt",
        entryType: 0,
        dna: "cc9265ecd0e3af242679ebc89f09a85bf86f110ce3ba8b991c74eafe9e2055dc",
        playerDetails: [
          {
            playerName: "Official Trump",
            playerSymbol: "trump",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/53746/large/trump.png?1737171561",
            positionIndex: 0,
            startPrice: 7.34627802,
            endPrice: 7.39774866,
            playerValue: 0.248025144,
            pythLazerId: 203,
            exponent: -8,
            pnlPercentage: 0.700636,
            multiplier: 77,
            isBot: false,
            playerPower: 77,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//trump.webp",
            priceChange7d: -5.87958,
          },
          {
            playerName: "Official Trump",
            playerSymbol: "trump",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/53746/large/trump.png?1737171561",
            positionIndex: 1,
            startPrice: 7.34627802,
            endPrice: 7.39774866,
            playerValue: 0.248025144,
            pythLazerId: 203,
            exponent: -8,
            pnlPercentage: 0.700636,
            multiplier: 77,
            isBot: false,
            playerPower: 77,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//trump.webp",
            priceChange7d: -5.87958,
          },
          {
            playerName: "Official Trump",
            playerSymbol: "trump",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/53746/large/trump.png?1737171561",
            positionIndex: 2,
            startPrice: 7.34627802,
            endPrice: 7.39774866,
            playerValue: 0.248025144,
            pythLazerId: 203,
            exponent: -8,
            pnlPercentage: 0.700636,
            multiplier: 77,
            isBot: false,
            playerPower: 77,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//trump.webp",
            priceChange7d: -5.87958,
          },
          {
            playerName: "Official Trump",
            playerSymbol: "trump",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/53746/large/trump.png?1737171561",
            positionIndex: 3,
            startPrice: 7.34627802,
            endPrice: 7.39774866,
            playerValue: 0.248025144,
            pythLazerId: 203,
            exponent: -8,
            pnlPercentage: 0.700636,
            multiplier: 77,
            isBot: false,
            playerPower: 77,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//trump.webp",
            priceChange7d: -5.87958,
          },
          {
            playerName: "Official Trump",
            playerSymbol: "trump",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/53746/large/trump.png?1737171561",
            positionIndex: 4,
            startPrice: 7.34627802,
            endPrice: 7.39774866,
            playerValue: 0.248025144,
            pythLazerId: 203,
            exponent: -8,
            pnlPercentage: 0.700636,
            multiplier: 77,
            isBot: false,
            playerPower: 77,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//trump.webp",
            priceChange7d: -5.87958,
          },
        ],
      },
      ownerProfile: {
        profilePicture:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/user-assets/profile-pics/6720-1757334695664.png",
        username: "soldegenmonke.skr",
        userPubkey: "25AHqZAtU2iZo8RXG3KAzrWTPpK319vcpxBJEuEwpdQt",
        isLegends: false,
        domainType: "seeker",
        activeFrameUrl:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/legend_frame.png",
      },
    },
    {
      squadAddress: "5187a7aa-98b2-4efe-b36e-07a9afaccca2",
      squadDetails: {
        name: "BT",
        index: 2,
        formation: "0-0-5",
        squadValue: 0.04347130000000001,
        squadMatchType: "short",
        owner: "EFigS9HSSgrskPpNFPv3uKpyMug4AQTvJ4aRpqbYTHi1",
        entryType: 0,
        dna: "12b847c63f3fb179c05077b2f8fd7abb030602719117d11eb88167d7450e2cfe",
        playerDetails: [
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 0,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: 0.008694260000000002,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: 0.023498,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 1,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: 0.008694260000000002,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: 0.023498,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 2,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: 0.008694260000000002,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: 0.023498,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 3,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: 0.008694260000000002,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: 0.023498,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 4,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: 0.008694260000000002,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: 0.023498,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
        ],
      },
      ownerProfile: {
        profilePicture:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/user-assets/profile-pics/8266-1758977480765.jpg",
        username: "miller.skr",
        userPubkey: "EFigS9HSSgrskPpNFPv3uKpyMug4AQTvJ4aRpqbYTHi1",
        isLegends: false,
        domainType: "seeker",
        activeFrameUrl:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/legend_frame.png",
      },
    },
    {
      squadAddress: "5187a7aa-98b2-4efe-b36e-07a9afaccca2",
      squadDetails: {
        name: "BT",
        index: 1,
        formation: "0-0-5",
        squadValue: -0.0035239500000000014,
        squadMatchType: "long",
        owner: "EFigS9HSSgrskPpNFPv3uKpyMug4AQTvJ4aRpqbYTHi1",
        entryType: 0,
        dna: "67e22d9f76f78f2fee0f319a481e388b75656439aac63f4afff7845d64b378eb",
        playerDetails: [
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 0,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: -0.0007047900000000003,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: -0.023493,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 1,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: -0.0007047900000000003,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: -0.023493,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 2,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: -0.0007047900000000003,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: -0.023493,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 3,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: -0.0007047900000000003,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: -0.023493,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
          {
            playerName: "aixbt by Virtuals",
            playerSymbol: "aixbt",
            playerImageUrl:
              "https://coin-images.coingecko.com/coins/images/51784/large/3.png?1731981138",
            positionIndex: 4,
            startPrice: 0.08509,
            endPrice: 0.08507001,
            playerValue: -0.0007047900000000003,
            pythLazerId: 140,
            exponent: -8,
            pnlPercentage: -0.023493,
            multiplier: 85,
            isBot: false,
            playerPower: 85,
            playerCard:
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/player-cards//aixbt.webp",
            priceChange7d: -16.61952,
          },
        ],
      },
      ownerProfile: {
        profilePicture:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/user-assets/profile-pics/8266-1758977480765.jpg",
        username: "miller.skr",
        userPubkey: "EFigS9HSSgrskPpNFPv3uKpyMug4AQTvJ4aRpqbYTHi1",
        isLegends: false,
        domainType: "seeker",
        activeFrameUrl:
          "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/legend_frame.png",
      },
    },
  ],
  pagination: {
    totalPlayers: 3,
    limit: 1000,
    offset: 0,
    hasMore: false,
    currentPage: 1,
    totalPages: 1,
  },
  nextStartEpoch: 1761660020,
  nextAvailableRushToJoinId: "3a377e72-ec13-4532-a4a1-d48374abc263",
};

const RUSH_SPARKLES = [
  { x: -50, y: -22, size: 10, delay: 0, color: undefined, shape: "diamond" },
  { x: -28, y: -50, size: 7, delay: 0.08, color: undefined, shape: "dot" },
  { x: 6, y: -60, size: 8, delay: 0.1, color: "#B8FFB0", shape: "dot" },
  { x: 34, y: -44, size: 7, delay: 0.06, color: undefined, shape: "diamond" },
  { x: 52, y: -10, size: 7, delay: 0.12, color: undefined, shape: "dot" },
  { x: 38, y: 28, size: 10, delay: 0.16, color: undefined, shape: "diamond" },
  { x: 0, y: 50, size: 8, delay: 0.14, color: undefined, shape: "dot" },
  { x: -38, y: 34, size: 7, delay: 0.18, color: undefined, shape: "dot" },
  { x: -60, y: -3, size: 5, delay: 0.04, color: undefined, shape: "diamond" },
] as const;

type TournamentPlayer = TournamentDetailsResponse["players"][number];

type TournamentPlayerJoinedEvent = {
  type: "tournament_player_joined";
  tournament_id: string;
  joined_count: number;
  player: TournamentPlayer | null;
  startDateEpoch?: number | null;
  endDateEpoch?: number | null;
};

type TournamentStatusChangedEvent = {
  type: "tournament_status_changed";
  tournament_id: string;
  old_status: string;
  new_status: string;
};

type TournamentScheduleChangedEvent = {
  type: "tournament_schedule_changed";
  tournament_id: string;
  start_date_epoch: number | null;
  end_date_epoch: number | null;
};

type TournamentSsePayload =
  | TournamentDetailsResponse
  | TournamentPlayerJoinedEvent
  | TournamentStatusChangedEvent
  | TournamentScheduleChangedEvent
  | { type?: string };

function Rush() {
  const queryClient = useQueryClient();
  const { user } = usePrivy();
  const router = useRouter();
  const { startNextStep, closeNextStep } = useNextStep();
  const [data, setData] = useState<TournamentDetailsResponse | null>(null);
  const [isSseErrorOpen, setIsSseErrorOpen] = useState(false);
  const lastTournamentIdRef = useRef<string | null>(null);
  const hasSnapshotRef = useRef(false);
  const { data: userStats, refetch: refetchUserStats } = useStats(
    user?.wallet?.address ?? "",
  );
  const rookieBlocked = false;
  const rushType = "rush" as const;
  const { data: initialConsecutive } = useTournamentConsecutive({
    refetchInterval: false,
    type: rushType,
  });
  const gameWalletAddress = userStats?.embeddedWallet;
  const userId = userStats?.userId;
  const { data: queueData, refetch: refetchQueueData } = useQueueList(
    userId,
    rushType,
    { refetchInterval: false },
  );
  const { data: solPrice } = useSolanaPrice();

  const joinSoundRef = useRef<HTMLAudioElement>(null);

  const [isPiggyActive, setIsPiggyActive] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [selectedPlayerRank, setSelectedPlayerRank] = useState<number | null>(
    null,
  );
  const [isEndedNavigationVisible, setIsEndedNavigationVisible] =
    useState(false);

  const [liveSquadValues, setLiveSquadValues] = useState<Map<string, number>>(
    new Map(),
  );

  // Play SFX when someone joins (players length increases)
  const prevPlayersCountRef = useRef<number | null>(null);
  const lastTourTriggerRef = useRef<string | null>(null);
  const [isRevealOpen, setIsRevealOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<Player | null>(null);
  const [isReportMode, setIsReportMode] = useState(false);

  useEffect(() => {
    let isActive = true;
    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/consecutive?type=${rushType}`,
    );

    const syncClock = (obj: Record<string, unknown> | null) => {
      if (obj && typeof obj.serverTime === "number") {
        updateClockOffset(obj.serverTime);
      }
    };

    const handleSnapshot = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentDetailsResponse & { serverTime?: number };
        syncClock(payload as unknown as Record<string, unknown>);
        if (!payload) return;

        if (lastTournamentIdRef.current !== payload.tournamentId) {
          prevPlayersCountRef.current = null;
          lastTournamentIdRef.current = payload.tournamentId;
        }

        setData(payload);
        setIsSseErrorOpen(false);
        hasSnapshotRef.current = true;
      } catch (error) {
        console.error("Failed to parse rush snapshot:", error);
        setIsSseErrorOpen(true);
      }
    };

    const handlePlayerJoined = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentPlayerJoinedEvent;

        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;

          let nextPlayers = prev.players;
          if (payload.player) {
            const exists = prev.players.some(
              (player) => player.squadAddress === payload.player?.squadAddress,
            );

            if (!exists) {
              nextPlayers = [...prev.players, payload.player];
            }
          }

          const joinedPlayers = nextPlayers.length;

          return {
            ...prev,
            joinedPlayers,
            players: nextPlayers,
          };
        });
      } catch (error) {
        console.error("Failed to parse rush player joined:", error);
      }
    };

    const handleStatusChanged = (event: Event) => {
      if (!isActive) return;
      // NOTE: Enable for debug
      // console.log("NEW STATUS PAYLOAD", event);
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentStatusChangedEvent;

        const isCurrentTournament =
          payload.tournament_id === lastTournamentIdRef.current;
        const hasStatusChanged = payload.old_status !== payload.new_status;
        if (isCurrentTournament && hasStatusChanged) {
          void refetchQueueData();
        }

        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          const updates: Partial<TournamentDetailsResponse> = {
            status: payload.new_status,
          };
          // When transitioning to completed, ensure nextOpenEpoch is in the
          // future so the "Next match in" countdown doesn't show 00:00:00
          // while waiting for the next snapshot.
          if (
            payload.new_status === "completed" &&
            prev.nextOpenEpoch <= Math.floor(getSyncedNow() / 1000)
          ) {
            updates.nextOpenEpoch = Math.floor(getSyncedNow() / 1000) + 5;
          }
          return {
            ...prev,
            ...updates,
          };
        });
      } catch (error) {
        console.error("Failed to parse rush status changed:", error);
      }
    };

    const handleScheduleChanged = (event: Event) => {
      if (!isActive) return;
      // NOTE: Enable for debug
      // console.log("NEW PAYLOAD", event);
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentScheduleChangedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;

          const target = dayjs.unix(payload.start_date_epoch!);
          const now = dayjs(getSyncedNow());
          const diff = target.diff(now, "second");

          // NOTE: Enable for debug
          // console.log("DIFF SCHEDULE", diff);
          return {
            ...prev,
            startDate: payload.start_date_epoch ?? prev.startDate,
            endDate: payload.end_date_epoch ?? prev.endDate,
          };
        });
      } catch (error) {
        console.error("Failed to parse rush schedule changed:", error);
      }
    };

    const handleStreamError = () => {
      if (!isActive) return;
      setIsSseErrorOpen(true);
    };

    const handleMessage = (event: MessageEvent<string>) => {
      if (!isActive) return;
      try {
        const payload = JSON.parse(event.data) as TournamentSsePayload;
        // NOTE: Enable for debug
        // console.log("NEW PAYLOAD", payload);
        if (!payload || typeof payload !== "object") return;
        if ("tournamentId" in payload) {
          handleSnapshot(event);
          return;
        }
        if ("type" in payload && payload.type) {
          if (payload.type === "tournament_player_joined") {
            handlePlayerJoined(event);
            return;
          }
          if (payload.type === "tournament_status_changed") {
            handleStatusChanged(event);
            return;
          }
          if (payload.type === "tournament_schedule_changed") {
            handleScheduleChanged(event);
          }
        }
      } catch (error) {
        console.error("Failed to parse rush message:", error);
      }
    };

    eventSource.addEventListener("snapshot", handleSnapshot);
    eventSource.addEventListener(
      "tournament_player_joined",
      handlePlayerJoined,
    );
    eventSource.addEventListener(
      "tournament_status_changed",
      handleStatusChanged,
    );
    eventSource.addEventListener(
      "tournament_schedule_changed",
      handleScheduleChanged,
    );
    eventSource.addEventListener("error", handleStreamError);
    eventSource.addEventListener("message", handleMessage);
    eventSource.onerror = handleStreamError;

    return () => {
      isActive = false;
      eventSource.close();
    };
  }, [rushType]);

  useEffect(() => {
    if (hasSnapshotRef.current || !initialConsecutive) return;
    setData(initialConsecutive);
  }, [initialConsecutive]);

  useEffect(() => {
    if (!data) return;

    const current = data.players.length;

    // skip the very first run to avoid playing on initial load
    if (
      prevPlayersCountRef.current !== null &&
      current > prevPlayersCountRef.current
    ) {
      // Set piggy to active state
      setIsPiggyActive(true);

      // Try to play; some browsers require prior user interaction
      joinSoundRef.current?.play().catch((err) => {
        // Silently ignore if autoplay is blocked
        console.debug("Join sound blocked by autoplay policy:", err);
      });

      // Reset piggy to idle after 2 seconds
      setTimeout(() => {
        setIsPiggyActive(false);
      }, 3000);
    }

    prevPlayersCountRef.current = current;
  }, [data]);

  const [isExtraTrophyOpen, setIsExtraTrophyOpen] = useState(false);

  useEffect(() => {
    if (!data) return;

    const isLiveLocal = data.status === "live";
    const isCompletedLocal = data.status === "completed";
    const hasNoPlayers = data.players && data.players.length === 0;
    const hasAtLeastOnePlayer = data.players && data.players.length >= 1;

    const isSkipped = localStorage.getItem(
      "cfl:rush-extra-trophy-tour-skipped",
    );

    if (
      hasNoPlayers &&
      !isLiveLocal &&
      !isCompletedLocal &&
      !!user &&
      !isSkipped
    ) {
      if (lastTourTriggerRef.current !== data.tournamentId) {
        lastTourTriggerRef.current = data.tournamentId;
        setIsExtraTrophyOpen(true);
      }
    } else if (
      hasAtLeastOnePlayer &&
      !isLiveLocal &&
      !isCompletedLocal &&
      !!user
    ) {
      const isFirstJoiner =
        data.players.length === 1 &&
        data.players[0]?.ownerProfile?.userPubkey === gameWalletAddress;

      const lastShownId = localStorage.getItem(
        "cfl:rush-claimed-tour-shown-id",
      );

      // if (isFirstJoiner && lastShownId !== data.tournamentId) {
      //   localStorage.setItem(
      //     "cfl:rush-claimed-tour-shown-id",
      //     data.tournamentId,
      //   );
      //   setIsRevealOpen(true);
      // }
    }
  }, [data, startNextStep, closeNextStep, user]);

  const isNewTournament = useMemo(() => {
    if (!data) return true;
    return data.joinedPlayers > 10;
  }, [data]);

  const hasNoPlayers = !!data && data.players.length === 0;
  const isLive = useMemo(() => data?.status === "live", [data]);
  const isEnded = useMemo(() => data?.status === "completed", [data]);
  const isWaiting = useMemo(() => data?.status === "waiting", [data]);


  const { play } = useMusic();
  const setRushTourMode = useSetAtom(rushTourModeAtom);
  const handleStartRushTour = useCallback(() => {
    const startTour = () => {
      try {
        closeNextStep();
        // eslint-disable-next-line no-empty
      } catch {}
      setRushTourMode("rush");
      startNextStep("rushWaitingTour");
    };

    const firstSelector = "[data-step='rush-timer']";
    const node = document.querySelector(firstSelector);

    if (node) {
      startTour();
      return;
    }

    setTimeout(() => {
      const retryNode = document.querySelector(firstSelector);
      if (retryNode) {
        startTour();
      }
    }, 300);
  }, [closeNextStep, setRushTourMode, startNextStep]);

  const setIsGameLive = useSetAtom(isGameLiveAtom);
  useEffect(() => {
    setIsGameLive(!!isLive);
    return () => setIsGameLive(false);
  }, [isLive, setIsGameLive]);

  useEffect(() => {
    if (isEnded) {
      queryClient.invalidateQueries({ queryKey: ["solana.getUsdcBalance"] });
    }
  }, [isEnded, queryClient]);

  useEffect(() => {
    if (isLive) {
      play({ ...MATCH_TRACK, autoPlay: true });

      return () => {
        play({ ...MENU_TRACK, autoPlay: true });
      };
    }
  }, [isLive, play]);

  const prizeData = useMemo(() => {
    if (!data) return [];
    const playerCount = data.players?.length ?? 0;
    return generatePrizeData(data.entryUsdc, playerCount, solPrice);
  }, [data, solPrice]);

  const handleSquadValuesUpdate = useCallback(
    (squadValues: Map<string, number>) => {
      setLiveSquadValues(squadValues);
    },
    [],
  );

  const getPositionLabel = (formation: string, positionIndex: number) => {
    const formationKey = formation as keyof typeof formations;
    return formations[formationKey]?.positions[positionIndex]?.label || "";
  };

  const sortedPlayers = useMemo(() => {
    if (!data?.players) return [];
    return [...data.players].sort((a, b) => {
      const aValue =
        (a.squadDetails as { squadValue?: number })?.squadValue || 0;
      const bValue =
        (b.squadDetails as { squadValue?: number })?.squadValue || 0;
      return bValue - aValue;
    });
  }, [data]);

  const reportedIds = (data?.players ?? [])
    .map((p) => p.ownerProfile.userId)
    .filter((id): id is number => id != null);
  const { data: canReportStatuses } = useCanReportBatch(
    reportedIds,
    userStats?.userId,
  );
  const canReportMap = new Map(
    canReportStatuses?.map((s) => [s.reportedId, s.canReport]) ?? [],
  );

  const sseErrorOverlay = (
    // <Activity mode={"visible"}>
    <Activity mode={isSseErrorOpen ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src={"/assets/connection-error.png"}
              alt="Info"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  if (!data || Object.keys(data).length === 0) {
    return (
      <>
        <AnimatedLoading />
        {sseErrorOverlay}
      </>
    );
  }

  // if (!queueData) return;

  const joinCount = data.players.filter(
    (player) => player.ownerProfile?.userPubkey === gameWalletAddress,
  ).length;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlayerClick = (player: any) => {
    setSelectedPlayer(player);
  };

  return (
    <div
      className="flex flex-col w-full h-full relative overflow-hidden bg-black"
      style={{
        imageRendering: "pixelated",
      }}
    >
      <ResourceBar
        className="absolute top-4 left-14 right-4 flex z-50 pl-3"
        rightExtra={
          !isLive && !isEnded ? (
            <HowItWorkButton
              // href="https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs/modes/rush"
              size="size-8"
              onClick={handleStartRushTour}
            />
          ) : undefined
        }
      />
      <FloatingLeftButton
        onClick={() => router.navigate({ to: `/home` })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <audio
        ref={joinSoundRef}
        src="https://cdn.navalabs.io/assets/bgm-join_NtqOlx.mp3"
        preload="auto"
      />
      {/* Rush Content - Waiting State */}
      {isWaiting && (
        <div
          className="h-full max-h-[60svh] flex-1 flex flex-col justify-center"
          style={{
            backgroundImage:
              "url(https://cdn.navalabs.io/assets/bg-rush_NLMfkn.PNG)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {!!userStats && (
            <div className="absolute top-20 left-4 z-50">
              <ClaimBorderDialog stats={userStats} refetch={refetchUserStats}>
                <DefaultButton
                  className={cn(
                    "flex flex-col items-center w-full! active:scale-95 hover:cursor-pointer",
                    "scale-95 sm:scale-100",
                  )}
                >
                  <div className="flex justify-center -mt-6">
                    <AvatarBorder
                      src={
                        "https://cdn.navalabs.io/icons/no-king-avatar_deezdj.png"
                      }
                      decoration={
                        "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/legend_frame.png"
                      }
                      size={40}
                    />
                  </div>
                  <div
                    className="flex flex-col items-center text-xs mt-2 leading-none uppercase max-w-10!"
                    style={{
                      textShadow: "black 0px 2px",
                    }}
                  >
                    {t("rush.claimFrame")}
                  </div>
                </DefaultButton>
              </ClaimBorderDialog>
            </div>
          )}

          <div
            className="flex flex-col items-center justify-center gap-4 absolute top-20 right-4 z-50"
            data-step="rush-daily"
          >
            <DefaultButton
              onClick={() => router.navigate({ to: "/rush/history" })}
              className="w-18 scale-95 sm:scale-100 flex flex-col items-center active:scale-95 hover:cursor-pointer"
            >
              <div className="flex justify-center -mt-6">
                <svg
                  className="size-8"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 22.0253V24L16 24V22.0253L8 22.0253Z"
                    fill="black"
                  />
                  <path
                    d="M4.0009 20.0506V22.0253L8 22.0253V20.0506H4.0009Z"
                    fill="black"
                  />
                  <path d="M2 16V20.0506H4.0009V16L2 16Z" fill="black" />
                  <path d="M16 22.0253H20V20.0506H16V22.0253Z" fill="black" />
                  <path
                    d="M20 20.0506H22V16H20V18.0253V20.0506Z"
                    fill="black"
                  />
                  <path d="M22 16H24V8H22V14V16Z" fill="black" />
                  <path d="M22 8V3.94937H20V8L22 8Z" fill="black" />
                  <path
                    d="M20 3.94937V1.97469L16 1.97468V3.94937H20Z"
                    fill="black"
                  />
                  <path
                    d="M8 1.90735e-06V1.97469V2L16 1.97468V0L8 1.90735e-06Z"
                    fill="black"
                  />
                  <path
                    d="M8 1.97469L4 1.97468V3.94937L6 3.94937L8 3.94937V2V1.97469Z"
                    fill="black"
                  />
                  <path d="M4 3.94937H2V8L4 8V3.94937Z" fill="black" />
                  <path
                    d="M8 20.0506V22.0253L16 22.0253V20.0506H8Z"
                    fill="#56C3FE"
                  />
                  <path d="M16 20.0506H20V18.0253H16V20.0506Z" fill="#56C3FE" />
                  <path d="M20 16H22V14H20V16Z" fill="#56C3FE" />
                  <path d="M4 3.94937V8H6V3.94937L4 3.94937Z" fill="#DBFDFF" />
                  <path
                    d="M16 3.94937V1.97468L8 2V3.94937H16Z"
                    fill="#DBFDFF"
                  />
                  <path d="M2 8L0 8V16H2V8Z" fill="black" />
                  <path d="M4 8L2 8V16L4.0009 16L4 8Z" fill="#DBFDFF" />
                  <path d="M14 14H6V12H12V6H14V14Z" fill="black" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.0009 16V20.0506H8H16V18.0253H20V16V14H22V8L20 8V3.94937H16H8L6 3.94937V8H4L4.0009 16ZM6 14H14V6H12V12H6V14Z"
                    fill="#55ECFF"
                  />
                </svg>
              </div>
              <div
                className="flex flex-col items-center text-xs mt-2 leading-none uppercase max-w-10!"
                style={{
                  textShadow: "black 0px 2px",
                }}
              >
                {t("rushHistory.viewHistory")}
              </div>
            </DefaultButton>
          </div>

          {/* Coin Piggy at top center */}
          <div
            className="flex flex-col items-center gap-1 mt-20"
            data-step="rush-timer"
          >
            <div className="w-[38%] sm:w-1/2 overflow-hidden">
              <img
                src={
                  isPiggyActive
                    ? "https://cdn.navalabs.io/icons/piggy_filled_dzrxqj.png"
                    : "https://cdn.navalabs.io/icons/piggy_idle_q1vaha.png"
                }
                style={{
                  imageRendering: "pixelated",
                  objectFit: "cover",
                }}
                alt={t("rush.altTexts.coinPiggy")}
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="flex items-center gap-2 -mt-2">
              <img
                alt=""
                src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                className="w-3.5 sm:w-4"
              />
              <Countdown
                className="font-extrabold text-yellow-500 text-xl sm:text-2xl tracking-wider"
                startTimestamp={Number(data.startDate)}
                isTrenches={true}
              />
            </div>
          </div>

          {/* Rolling column machine */}
          <div
            className="relative mx-8 scale-90 sm:scale-100"
            data-step="rush-prizes"
          >
            <img
              alt={t("rush.altTexts.rushReelFrame")}
              className="relative w-full z-10 pointer-events-none"
              src={"https://cdn.navalabs.io/rolling_list_frame_xokld8.png"}
            />
            <style>{`
              @keyframes rush-reward-pop {
                0% {
                  transform: scale(0.85) rotate(-4deg);
                  opacity: 0.8;
                }
                18% {
                  transform: scale(1.08) rotate(3deg);
                  opacity: 1;
                }
                28% {
                  transform: scale(0.97) rotate(-1deg);
                }
                36%, 100% {
                  transform: scale(1) rotate(0deg);
                  opacity: 1;
                }
              }
              @keyframes discon-spin {
                0% {
                  transform: rotate(0deg) scale(1.085);
                }
                22% {
                  transform: rotate(360deg) scale(1.085);
                }
                28% {
                  transform: rotate(360deg) scale(1);
                }
                100% {
                  transform: rotate(360deg) scale(1);
                }
              }
              @keyframes discon-sparkle {
                0% {
                  transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
                  opacity: 0;
                }
                8% {
                  opacity: 1;
                }
                22% {
                  transform: translate(
                      calc(-50% + var(--x)),
                      calc(-50% + var(--y))
                    )
                    scale(1.2)
                    rotate(30deg);
                  opacity: 0;
                }
                100% {
                  opacity: 0;
                }
              }
            `}</style>
            <ItemExtraTrophyDialog
              title={t("rush.rushExtraTrophy.title")}
              description={t("rush.rushExtraTrophy.description")}
              ctaLabel={t("rush.rushExtraTrophy.ctaLabel")}
              trophyValue={500}
              headerText={t("rush.rushExtraTrophy.headerText")}
              claimableStatus={hasNoPlayers}
            >
              <button className="absolute -top-5 right-0.5 z-10">
                <div className="relative">
                  <img
                    src="/assets/discon_green.png"
                    className="h-16 w-auto"
                    alt="discon_green.png"
                    style={{
                      animation: hasNoPlayers ? "discon-spin 4.5s linear infinite" : "none",
                      imageRendering: "pixelated",
                    }}
                  />
                  <div className="mt-1.5 z-10 absolute inset-0 flex flex-col items-center justify-center">
                    <img
                      alt=""
                      src={REWARD_ICONS[1]}
                      className="h-5.5 w-auto"
                      style={{
                        animation:
                          "rush-reward-pop 1.55s cubic-bezier(0.16, 1, 0.3, 1) infinite",
                      }}
                    />
                    <span
                      className="-mt-2.5 z-2 text-base"
                      style={{
                        textShadow: generateTextShadow(0.45, "#1D6A3C"),
                      }}
                    >
                      +500
                    </span>
                  </div>
                  {hasNoPlayers && <div className="pointer-events-none absolute inset-0 z-20">
                    {RUSH_SPARKLES.map((sparkle, i) => (
                      <div
                        key={i}
                        className={`absolute left-1/2 top-1/2 ${
                          sparkle.shape === "diamond" ? "rotate-45" : ""
                        }`}
                        style={
                          {
                            animation: `discon-sparkle 4.5s ease-out ${sparkle.delay}s infinite`,
                            "--x": `${sparkle.x}px`,
                            "--y": `${sparkle.y}px`,
                          } as CSSProperties
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 480 480"
                          style={{
                            width: `${sparkle.size}px`,
                            height: `${sparkle.size}px`,
                            color: sparkle.color ?? "var(--color-yellow-200)",
                            filter: "drop-shadow(0 0 8px currentColor)",
                          }}
                        >
                          <path
                            d="M480 240A240 240 0 0 1 240 0 240 240 0 0 1 0 240a240 240 0 0 1 240 240 240 240 0 0 1 240-240Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    ))}
                  </div>}
                </div>
              </button>
            </ItemExtraTrophyDialog>
            <RollingList
              prizeData={prizeData}
              entryUsdc={data.entryUsdc}
              solPrice={solPrice}
              joinedPlayers={data.players?.length ?? 0}
              minPlayers={3}
            />
          </div>

          {queueData && (
            <QueuePlayerSheet queueData={queueData}>
              <div
                data-step="rush-queue"
                className={cn(
                  queueData.hasActiveQueue ? "visible" : "invisible",
                  "flex flex-col items-center pb-4 gap-1 mt-1 mx-12",
                )}
              >
                <div className="default-btn-container relative cursor-pointer hover:opacity-90 transition-opacity w-full px-1! sm:px-2!">
                  {queueData.hasActiveQueue ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-nowrap text-xs sm:text-sm font-bold text-white leading-4">
                          {t("rush.yourQueue")}
                        </span>
                        <div className="flex justify-center items-center gap-1 -mt-0.5">
                          {Array.from(
                            {
                              length: queueData.roundCount,
                            },
                            (_, index) => {
                              const NUM_ITEM_SHOWED = 4;
                              const roundNumber = index + 1;
                              if (roundNumber < queueData.currentRound - 1)
                                return <></>;
                              if (
                                roundNumber >=
                                queueData.currentRound + NUM_ITEM_SHOWED
                              )
                                return <></>;
                              return (
                                <div
                                  key={index}
                                  className={cn(
                                    "w-5 h-5 flex items-center justify-center text-xs sm:text-sm",
                                    index + 1 === queueData.currentRound - 1
                                      ? "opacity-50"
                                      : "opacity-100",
                                    index + 1 === queueData.currentRound
                                      ? "bg-[#2AAAFC] text-white"
                                      : "bg-[#1D2A52] text-white",
                                  )}
                                  style={{
                                    clipPath: `
                                          polygon(
                                            /* top-left cut */
                                            0% 10%, 5% 10%, 5% 5%, 10% 5%, 10% 0%,
                                            /* top-right cut */
                                            90% 0%, 90% 5%, 95% 5%, 95% 10%, 100% 10%,
                                            /* bottom-right cut */
                                            100% 90%, 95% 90%, 95% 95%, 90% 95%, 90% 100%,
                                            /* bottom-left cut */
                                            10% 100%, 10% 95%, 5% 95%, 5% 90%, 0% 90%
                                          )
                                        `,
                                  }}
                                >
                                  <span
                                    className={cn(
                                      "text-xs font-black leading-none",
                                    )}
                                  >
                                    #{roundNumber}
                                  </span>
                                </div>
                              );
                            },
                          )}
                          {queueData.roundCount - queueData.currentRound - 3 >
                            0 && (
                            <span className="text-nowrap text-white text-xs font-bold">
                              +
                              {queueData.roundCount -
                                queueData.currentRound -
                                3}{" "}
                              {t("rush.more")}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 -mt-1 invisible">
                      <span className="text-sm font-bold text-zinc-400">
                        {t("rush.youHaveNoQueuedSquad")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </QueuePlayerSheet>
          )}
          {/* Auto Join and Queue Display */}
        </div>
      )}

      {/* Rush Content - Live State */}
      {isLive && (
        // <Activity mode={isLive ? "visible" : "hidden"}>
        <div className="relative max-h-[60%] flex-1 flex flex-col overflow-hidden gap-2 pt-14">
          <div className="absolute top-16 w-full flex flex-col items-center justify-center z-10">
            <p className="text-sm text-white leading-4 uppercase">
              {t("rush.endingIn")}
            </p>
            <div className="flex items-center justify-center rounded-full">
              <Countdown
                className="font-extrabold text-yellow-400 text-xl tracking-wider"
                startTimestamp={+data.endDate}
              />
            </div>
          </div>

          {/*{isNewTournament && (
              <ArenaBar
                players={data.players}
                tournament={data}
                onPlayerClick={handlePlayerClick}
                onSquadValuesUpdate={handleSquadValuesUpdate}
              />
            )}
            {!isNewTournament && (
              <ArenaChartECharts
                players={data.players}
                onSquadValuesUpdate={handleSquadValuesUpdate}
              />
            )}*/}
          <ArenaChartECharts
            players={data.players}
            onSquadValuesUpdate={handleSquadValuesUpdate}
            matchStartedAt={
              data.endDate
                ? (Number(data.endDate) - data.duration) * 1000
                : undefined
            }
          />
        </div>
        // </Activity>
      )}

      {isEnded && (
        <div
          // className="max-h-[60%] flex-1 flex flex-col h-full justify-between gap-2 overflow-scroll p-1"
          className="relative flex-1 flex flex-col h-full justify-between gap-2 overflow-scroll p-1"
          style={{
            backgroundImage: `url('https://cdn.navalabs.io/backgrounds/bg_rush_completed_vk6swr.png')`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            imageRendering: "pixelated",
          }}
        >
          <Activity
            mode={
              sortedPlayers[0].squadDetails.playerDetails[0].endPrice === 0
                ? "visible"
                : "hidden"
            }
          >
            <div className="absolute inset-0 flex items-center justify-center py-6 bg-black/50 z-10">
              <img
                alt=""
                src="/assets/loading-pixel.png"
                className="w-1/8"
                style={{
                  imageRendering: "pixelated",
                }}
              />
            </div>
          </Activity>

          {/* <ReportModeToggle
            isActive={isReportMode}
            onToggle={() => setIsReportMode((prev) => !prev)}
          /> */}

          <div className="flex flex-col gap-1 items-center mt-16">
            <div className="flex items-center gap-2">
              <span className="text-3xl uppercase font-semibold text-shadow-black">
                {t("rush.nextMatchIn")}
              </span>
            </div>
            <div className="flex black-container items-center -pl-4 pr-1 gap-2">
              <img
                alt=""
                src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                className="w-4"
              />
              <Countdown
                className="font-extrabold text-white text-base leading-0 tracking-wider"
                startTimestamp={data.nextOpenEpoch}
              />
            </div>
            {isReportMode && (
              <p
                className="text-white text-sm font-bold mt-1 uppercase animate-pulse"
                style={{ textShadow: generateTextShadow(1) }}
              >
                {t("tournamentEnded.selectPlayerToReport")}
              </p>
            )}
          </div>

          <ScrollArea className="flex-1 h-full!" type="auto">
            <div className="absolute inset-0 flex flex-col items-stretch gap-2">
              {sortedPlayers.map((player, index) => {
                const rank = index + 1;
                const isSelf =
                  gameWalletAddress === player.ownerProfile.userPubkey;

                const getPrizesForRank = (rank: number) => {
                  const prizes: Array<{
                    icon: string;
                    value: string | number;
                    alt?: string;
                    isPositive?: boolean;
                  }> = [];

                  if (data.payoutList?.payout) {
                    for (const payout of data.payoutList.payout) {
                      const isInRange =
                        rank >= payout.range.min && rank <= payout.range.max;
                      if (isInRange) {
                        for (const reward of payout.rewards) {
                          // Map itemId directly to REWARD_ICONS array
                          // itemId 0 = USD (REWARD_ICONS[0]), itemId 1 = Trophy (REWARD_ICONS[1])
                          prizes.push({
                            icon: REWARD_ICONS[reward.itemId],
                            value: reward.amount,
                            isPositive: true,
                          });
                        }
                      }
                    }
                  }

                  return prizes;
                };
                const winnerPrizes = getPrizesForRank(rank);
                const isWinner = winnerPrizes.length > 0;
                const prizes = [...winnerPrizes];

                if (!isWinner) {
                  const loserTrophies = data.loserTrophies ?? 0;

                  if (loserTrophies > 0) {
                    prizes.push({
                      icon: REWARD_ICONS[1],
                      value: loserTrophies,
                      alt: t("rush.altTexts.trophy"),
                      isPositive: true,
                    });
                  }
                }
                return (
                  <div
                    key={index}
                    className={cn(
                      "relative transition-opacity duration-200",
                      isReportMode &&
                        isSelf &&
                        "opacity-40 pointer-events-none",
                    )}
                  >
                    <UserRankList
                      player={player}
                      rank={rank}
                      isWinner={isWinner}
                      prizes={prizes}
                      isSelf={isSelf}
                      canReport={
                        canReportMap.get(player.ownerProfile.userId ?? -1) ??
                        false
                      }
                      isReportMode={isReportMode && !isSelf}
                      onClick={() => {
                        if (isReportMode) {
                          if (!isSelf) {
                            setReportTarget(player);
                          }
                        } else {
                          setSelectedPlayer(player);
                          setSelectedPlayerRank(rank);
                          setIsEndedNavigationVisible(true);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Navigation - 40% (conditional based on status) */}
      {isLive && (
        <RushLiveControl
          tournamentDetails={data}
          className="h-full max-h-[40svh]"
          onSquadSelect={handlePlayerClick}
          selectedPlayer={selectedPlayer}
          liveSquadValues={liveSquadValues}
          refetchQueueData={refetchQueueData}
          // refetchQueueData={(() => {}) as unknown}
        />
      )}
      {isWaiting && (
        <NavigationRush
          key={data.tournamentId}
          tournamentDetails={data}
          entryFee={data.entryUsdc}
          tournamentId={data.tournamentId}
          className="h-full max-h-[40svh]"
          refetchQueueData={refetchQueueData}
          rushType={rushType}
          rookieBlocked={rookieBlocked}
        />
      )}
      {isEnded && isEndedNavigationVisible && (
        <NavigationTournamentEnded
          tournament={data}
          sortedPlayers={sortedPlayers}
          selectedPlayer={selectedPlayer}
          selectedPlayerRank={selectedPlayerRank}
          onBack={() => {
            setSelectedPlayer(null);
            setSelectedPlayerRank(null);
            setIsEndedNavigationVisible(false);
          }}
          className="h-full max-h-[40svh]"
          isRushMode={true}
        />
      )}

      {/* <RevealOverlay
        isOpen={isRevealOpen}
        onClose={() => setIsRevealOpen(false)}
        title={t("rush.congratulations")}
        rewardText={t("rush.rewards.rush")}
        rewardValue={500}
        buttonText={t("rush.claimPrize")}
      /> */}

      {/* <InfoOverlay */}
      {/*   isOpen={isExtraTrophyOpen} */}
      {/*   onClose={() => setIsExtraTrophyOpen(false)} */}
      {/*   onSkip={() => { */}
      {/*     setIsExtraTrophyOpen(false); */}
      {/*     localStorage.setItem("cfl:rush-extra-trophy-tour-skipped", "true"); */}
      {/*   }} */}
      {/*   imageHeader={t("rush.rushInfoOverlay.extra")} */}
      {/*   title={t("rush.rushInfoOverlay.title")} */}
      {/*   message={t("rush.rushInfoOverlay.message")} */}
      {/*   imageSrc="/assets/extra-trophy-icon.png" */}
      {/*   rewardValue={500} */}
      {/* /> */}
      {sseErrorOverlay}
      <ReportPlayerDialog
        open={reportTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReportTarget(null);
            setIsReportMode(false);
          }
        }}
        reportedId={reportTarget?.ownerProfile.userId}
        reportedUsername={reportTarget?.ownerProfile.username}
        reportedProfilePicture={reportTarget?.ownerProfile.profilePicture}
        reportedFrameUrl={reportTarget?.ownerProfile.activeFrameUrl}
        reportedIsVerified={reportTarget?.ownerProfile.isVerified}
        canReportPlayer={
          canReportMap.get(reportTarget?.ownerProfile.userId ?? -1) ?? false
        }
        proofId={data?.tournamentId}
        playerDetails={reportTarget?.squadDetails.playerDetails}
      />
    </div>
  );
}
